import { InfoCircleFilled } from "@ant-design/icons";
import { Button, Space, Tag, Tooltip } from "antd";
import MaterialIcon from "components/svg-icons/material-icon";
import {
  TEditingEntity,
  TEntityLink,
  TLinkSource,
  TUnlinkedItem
} from "components/feature-entity-linking-page/models";
import { useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getFeatureDetail,
  getLinkType
} from "components/feature-entity-linking-page/utils";
import { FeatureTypes } from "entity-app/constants";
import { specSectionLabel } from "components/cell-renders";
import FeatureRowActivityCard from "./feature-row-activity-card";
import PredecessorSuccessorTag from "./material-predecessor-successor-tag";

function FeatureRowMaterialCard({
  materialLink,
  isEditing,
  onRemoveRow,
  editingEntity,
  setEditingEntity,
  featureTypeId,
  selectedEntityInfo,
  hidePredecessorTag = false
}: {
  materialLink: TLinkSource;
  isEditing: boolean;
  onRemoveRow: (rowId: string) => void;

  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  featureTypeId: FeatureTypes;
  selectedEntityInfo: TEntityLink;
  /** Associated Materials already shows the direction in a section sub-header. */
  hidePredecessorTag?: boolean;
}) {
  const { projectId } = useParams() as any;
  const material = materialLink.feature_instance!;

  const specDetail = useMemo(() => {
    const specSection = material.feature_instance_spec_sections.length
      ? material.feature_instance_spec_sections[0].spec_section
      : {
          section_number: "",
          title: ""
        };

    const specString = specSectionLabel({
      specNumber: specSection.section_number,
      specName: specSection.title
    });

    return {
      ...specSection,
      specString
    };
  }, [material.feature_instance_spec_sections]);

  const linkedActivities = useMemo(() => {
    return material.links.filter((x) => x.feature_instance?.feature.id === 9);
  }, [material.links]);

  const onRemoveSubActivityRow = useCallback(
    (rowId: string) => {
      setEditingEntity((pre) => {
        if (!pre) return pre;

        // Deep clone the list → prevents mutating nested objects
        const newList = structuredClone(pre.list);

        const materialLinkObj = newList.find(
          (x: TLinkSource) =>
            x.featureInstanceId === materialLink.featureInstanceId
        );

        // Find the activity being removed to add to unlinked buffer
        const removedActivity = materialLinkObj?.feature_instance?.links?.find(
          (x: TLinkSource) => x.featureInstanceId === rowId
        );

        if (materialLinkObj?.feature_instance) {
          materialLinkObj.feature_instance.links =
            materialLinkObj.feature_instance.links?.filter(
              (x: TLinkSource) => x.featureInstanceId !== rowId
            ) || [];
        }

        // Track the unlinked activity in the buffer
        const newUnlinkedItems: TUnlinkedItem[] = [];
        if (removedActivity) {
          const alreadyUnlinked = pre.unlinkedItems.some(
            (u) =>
              u.item.featureInstanceId === rowId &&
              u.parentMaterialId === materialLink.featureInstanceId
          );
          if (!alreadyUnlinked) {
            newUnlinkedItems.push({
              item: removedActivity,
              parentMaterialId: materialLink.featureInstanceId
            });
          }
        }

        return {
          ...pre,
          list: newList,
          unlinkedItems: [...pre.unlinkedItems, ...newUnlinkedItems]
        };
      });
    },
    [materialLink.featureInstanceId, setEditingEntity]
  );
  const onOffsetChange = useCallback(
    ({
      offset,
      activityLink
    }: {
      offset: number | null;
      activityLink: TLinkSource;
    }) => {
      const activityLinkedItem = structuredClone(activityLink);
      activityLinkedItem.offset_duration = offset;
      setEditingEntity((pre) => {
        if (!pre) return pre;

        // Deep clone the list → prevents mutating nested objects
        const newList = structuredClone(pre.list);

        const materialLinkObj = newList.find(
          (x: TLinkSource) =>
            x.featureInstanceId === materialLink.featureInstanceId
        );
        if (
          materialLinkObj &&
          materialLinkObj.feature_instance &&
          materialLinkObj.feature_instance.links
        ) {
          const indexActivity =
            materialLinkObj.feature_instance.links.findIndex(
              (x: TLinkSource) =>
                x.featureInstanceId === activityLink.featureInstanceId
            );

          // `|| []` fallback unreachable: enclosing `if` already guarded that
          // materialLinkObj.feature_instance.links is truthy.
          /* v8 ignore next */
          const activityLinks = materialLinkObj.feature_instance.links || [];

          materialLinkObj.feature_instance.links = [
            ...activityLinks.slice(0, indexActivity),
            activityLinkedItem,
            ...activityLinks.slice(indexActivity + 1)
          ];
        }

        return { ...pre, list: newList };
      });
    },
    [materialLink.featureInstanceId, setEditingEntity]
  );

  const updateReverseLinking = useCallback(
    ({ activityLink }: { activityLink: TLinkSource }) => {
      const activityLinkedItem = structuredClone(activityLink);
      activityLinkedItem.reverse_dependency = !activityLink.reverse_dependency;
      activityLinkedItem.link_type = getLinkType(
        activityLinkedItem.reverse_dependency,
        activityLinkedItem.isLinkedWithStartdate
      );

      if (activityLinkedItem.reverse_dependency) {
        activityLinkedItem.offset_duration = null;
      }

      setEditingEntity((pre) => {
        if (!pre) return pre;

        // Deep clone the list → prevents mutating nested objects
        const newList = structuredClone(pre.list);

        const materialLinkObj = newList.find(
          (x: TLinkSource) =>
            x.featureInstanceId === materialLink.featureInstanceId
        );
        if (
          materialLinkObj &&
          materialLinkObj.feature_instance &&
          materialLinkObj.feature_instance.links
        ) {
          const indexActivity =
            materialLinkObj.feature_instance.links.findIndex(
              (x: TLinkSource) =>
                x.featureInstanceId === activityLink.featureInstanceId
            );

          // `|| []` fallback unreachable: enclosing `if` already guarded that
          // materialLinkObj.feature_instance.links is truthy.
          /* v8 ignore next */
          const activityLinks = materialLinkObj.feature_instance.links || [];

          materialLinkObj.feature_instance.links = [
            ...activityLinks.slice(0, indexActivity),
            activityLinkedItem,
            ...activityLinks.slice(indexActivity + 1)
          ];
        }

        return { ...pre, list: newList };
      });
    },
    [materialLink.featureInstanceId, setEditingEntity]
  );

  const showPredecessorText = () => {
    if (
      featureTypeId === FeatureTypes.MATERIALS &&
      selectedEntityInfo &&
      (selectedEntityInfo?.predecessor_material_links?.length ?? 0) > 0
    )
      return true;
    return false;
  };

  const isMaterialLinkingPage = featureTypeId === FeatureTypes.MATERIALS;

  return (
    <div className="flex flex-col w-full features-card">
      <div className="border border-color px-1 py-2 bg-white rounded flex flex-col mb-2">
        <div
          className="flex items-center pr-1"
          data-testid="featurerowmaterialcard-header"
        >
          <MaterialIcon
            className="mr grow-none"
            size={20}
            fill="var(--ciq-gray-500)"
            data-testid="featurerowmaterialcard-material-icon"
          />
          <Tooltip
            placement="topLeft"
            title={material.title}
            data-testid="featurerowmaterialcard-tooltip"
          >
            {/* Single child: antd v6 drops the Tooltip's own className and wraps
                multiple children in a bare inline <span>, breaking this row's
                layout. Keep flex-grow + truncate on an explicit wrapper. */}
            <div className="flex-grow truncate">
              <Link
                className="grow-none"
                to={`/project/${projectId}/materials/${material?.id}`}
                data-testid="featurerowmaterialcard-link"
              >
                <i
                  className="entity-id-with-spec"
                  data-testid="featurerowmaterialcard-auto-inc-value"
                >
                  {material?.auto_inc_value}
                </i>
              </Link>

              <i
                className="mr-2 entity-id-with-spec"
                data-testid="featurerowmaterialcard-spec-string"
              >
                {specDetail.specString}
              </i>
              {materialLink?.governing_link && !showPredecessorText() ? (
                <span
                  className="text-base mr-1 pt-[10px] leading-[0]"
                  data-testid="featurerowmaterialcard-governing-link-indicator"
                >
                  *
                </span>
              ) : (
                ""
              )}
              <span data-testid="featurerowmaterialcard-material-title">
                {material.title}
              </span>
            </div>
          </Tooltip>
          <div
            className="flex items-center space-x-2"
            data-testid="featurerowmaterialcard-actions"
          >
            {showPredecessorText() && !hidePredecessorTag && (
              <PredecessorSuccessorTag
                title="Predecessor"
                data-testid="featurerowmaterialcard-predecessor-tag"
              />
            )}
            {isEditing && !material?.implicit && (
              <Space
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  onRemoveRow(material.id);
                }}
                className="unlink-button grow-none skip-listing-window"
                data-testid="featurerowmaterialcard-unlink-button"
              >
                Unlink
              </Space>
            )}

            {material.implicit && (
              <Tag
                className="italic rounded"
                data-testid="featurerowmaterialcard-system-generated-tag"
              >
                System Generated
              </Tag>
            )}
          </div>
        </div>
        {isEditing && material?.implicit && (
          <div className="px-2 pt-2">
            <div className="flex w-full border border-solid rounded-lg border-ciq-warning-border p-1 bg-ciq-warning-bg">
              <div
                className="px-2 flex items-center justify-center"
                data-testid="featurerowmaterialcard-warning-icon"
              >
                <InfoCircleFilled className="text-ciq-warning-text hover:text-ciq-warning-text m-px" />
              </div>
              <div
                className="text-black text-opacity-80"
                data-testid="featurerowmaterialcard-warning-text"
              >
                This planning-only Material will be removed when more Materials
                are linked. To keep this Material, add it from this Submittal’s
                Details page.
              </div>
            </div>
          </div>
        )}
      </div>

      {!isMaterialLinkingPage && linkedActivities?.length > 0 && (
        <div
          className="pl-6"
          data-testid="featurerowmaterialcard-linked-activities"
        >
          {linkedActivities.map((activity) => (
            <FeatureRowActivityCard
              item={activity}
              key={activity.feature_instance?.id}
              isEditing={isEditing}
              isAllowSetting
              onRemoveRow={onRemoveSubActivityRow}
              onOffsetChange={onOffsetChange}
              updateReverseLinking={updateReverseLinking}
            />
          ))}
        </div>
      )}
      {isEditing && !isMaterialLinkingPage && (
        <div className="flex justify-end pb-2 -mt-0.5">
          <Button
            className="skip-listing-window"
            size="small"
            data-testid="featurerowmaterialcard-button-associate-activity"
            // disabled={!canEdit || isDeletedSubmittal}
            onClick={() => {
              if (editingEntity)
                setEditingEntity({
                  ...editingEntity,
                  parentEntity: {
                    link: materialLink,
                    rightFeatureDetail: getFeatureDetail(
                      FeatureTypes.ACTIVITIES
                    )
                  }
                });
            }}
          >
            + Associate Activity
          </Button>
        </div>
      )}
    </div>
  );
}

export default FeatureRowMaterialCard;
